import { MutableRefObject, useCallback, useEffect, useMemo, useState } from "react";
import html2pdf from "html2pdf.js";
import { Person, SurgeonWithTissues } from "../../models";
import { useToasts } from "../../contexts";

interface UseSendTissueReportsParams {
    refs: MutableRefObject<(HTMLDivElement | null)[]>;
    data: SurgeonWithTissues[];
    dateRange: string;
    onUpload?: (data: SurgeonWithTissues, formData: FormData) => Promise<void>;
}

export const useSendTissueReports = ({ refs, data, dateRange, onUpload }: Readonly<UseSendTissueReportsParams>) => {
    const { errorToast, showToast } = useToasts();
    const [uploadingStatuses, setUploadingStatuses] = useState<Record<number, boolean>>({});
    const [isUploadInitiated, setIsUploadInitiated] = useState(false);
    const [hasError, setHasError] = useState(false);
    const isUploading = useMemo(() => Object.values(uploadingStatuses).some(Boolean), [uploadingStatuses]);

    useEffect(() => {
        if (isUploading) {
            setIsUploadInitiated(true);
        }
    }, [isUploading]);

    useEffect(() => {
        if (isUploadInitiated && !isUploading) {
            setIsUploadInitiated(false);
            if (!hasError) {
                showToast({
                    name: "reports-sent",
                    titleInHeader: "Success",
                    body: "Reports have been sent to surgeons.",
                    theme: "success",
                });
                setIsUploadInitiated(false);
            } else {
                errorToast(`There was a problem ${onUpload ? "sending" : "downloading"} the reports.`);
                setHasError(false);
            }
        }
    }, [isUploadInitiated, isUploading, hasError, showToast, errorToast, onUpload]);

    const getFileName = useCallback(
        (surgeon: Person) => {
            return `${surgeon.firstName} ${surgeon.lastName} - ${dateRange}.pdf`;
        },
        [dateRange]
    );

    const getOptions = useCallback((filename: string) => {
        const options = {
            margin: [10, 5],
            filename,
            pagebreak: { mode: "avoid-all" },
            jsPDF: { format: "letter", orientation: "portrait" },
            html2canvas: { scale: 2 },
        };
        return options;
    }, []);

    const addPageNumbers = useCallback((pdf: any) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const lastLineY = pdf.internal.pageSize.getHeight() - 3;

        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(10);
            pdf.setTextColor(0);
            pdf.text(`Page ${i} of ${totalPages}`, pdf.internal.pageSize.getWidth() - 25, lastLineY);
        }
        return pdf;
    }, []);

    const handlePdf = useCallback(
        (divElement: HTMLDivElement | null, data: SurgeonWithTissues) => {
            if (!divElement) return;

            const content = divElement.cloneNode?.(true);
            if (!content) return;

            const { surgeon } = data;

            if (onUpload) {
                html2pdf()
                    .set(getOptions(getFileName(surgeon)))
                    .from(content)
                    .toPdf()
                    .get("pdf")
                    .then((pdf: any) => addPageNumbers(pdf))
                    .get("pdf")
                    .then((pdf: any) => {
                        const uriString = pdf.output("datauristring");
                        const base64 = uriString.split("base64,")[1];
                        const binaryData = atob(base64);
                        const byteArray = new Uint8Array(binaryData.length);
                        for (let i = 0; i < binaryData.length; i++) {
                            byteArray[i] = binaryData.charCodeAt(i);
                        }
                        const file = new File([byteArray], getFileName(surgeon), { type: "application/pdf" });
                        const formData = new FormData();
                        formData.append("file", file);

                        setUploadingStatuses((prev) => ({ ...prev, [surgeon.id]: true }));
                        onUpload(data, formData)
                            .then(() => {
                                setUploadingStatuses((prev) => ({ ...prev, [surgeon.id]: false }));
                            })
                            .catch((e: unknown) => {
                                setHasError(true);
                                setUploadingStatuses((prev) => ({ ...prev, [surgeon.id]: false }));
                            });
                    })
                    .catch((e: unknown) => {
                        setHasError(true);
                        setUploadingStatuses((prev) => ({ ...prev, [surgeon.id]: false }));
                    });
            } else {
                html2pdf()
                    .set(getOptions(getFileName(surgeon)))
                    .from(content)
                    .toPdf()
                    .get("pdf")
                    .then((pdf: any) => addPageNumbers(pdf))
                    .save()
                    .catch((e: unknown) => {
                        console.error(e);
                        setHasError(true);
                    });
            }
        },
        [addPageNumbers, getOptions, onUpload, getFileName]
    );

    const sendPdfs = useCallback(() => {
        refs.current.forEach((ref, index) => {
            handlePdf(ref, data[index]);
        });
    }, [handlePdf, refs, data]);

    return useMemo(() => ({ sendPdfs, isUploading }), [sendPdfs, isUploading]);
};
